import React, { useRef, useEffect, useMemo } from 'react'
import { Box, Flex, Text, Button } from '@chakra-ui/core'
import { ConversationMessage } from './ConversationModal'
import { Message } from './Message'
import { IPop } from 'constants/interfaces'
import { BotThinkingBubble } from './BotThinkingBubble'
import { AlarmClock } from 'lucide-react'
import useLocaleText from 'components/useLocaleText'
import { NoFocusButton } from 'components/CommonComponents/NoFocusButton'

interface ConversationBodyProps {
  conversation: ConversationMessage[]
  isBotResponseInProgress: boolean
  isWebSocketReady: boolean
  onReconnect?: () => void
  hasWebSocketError?: boolean
  pop?: IPop
  isLoading?: boolean
  isSessionExpired?: boolean
  onReloadSession?: () => void
}

export const ConversationBody = ({
  conversation,
  isBotResponseInProgress,
  isWebSocketReady,
  onReconnect,
  hasWebSocketError = false,
  pop,
  isLoading,
  isSessionExpired = false,
  onReloadSession,
}: ConversationBodyProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const memoizedPop = useMemo(() => pop, [pop])
  const memoizedConversation = useMemo(() => conversation, [conversation])

  const crash_go_back = useLocaleText('crash_go_back')
  const session_expired = useLocaleText('session_expired')

  // Auto scroll to the last message
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [conversation, isBotResponseInProgress])

  // Session expired message
  if (isSessionExpired) {
    return (
      <Flex direction="column" align="center" justify="center" height="300px" px="20px">
        <Flex
          direction="column"
          align="center"
          justify="center"
          backgroundColor="#fff3cd"
          border="1px solid #ffeaa7"
          borderRadius="12px"
          p="20px"
          textAlign="center"
          maxW="400px"
        >
          <AlarmClock size={48} color="#856404" style={{ marginBottom: '12px' }} />
          <Text fontSize="16px" fontWeight="500" mb="8px" color="#856404">
            {session_expired}
          </Text>
          <NoFocusButton
            backgroundColor="#28a745"
            color="white"
            _hover={{ backgroundColor: '#218838' }}
            onClick={onReloadSession}
            size="sm"
          >
            {crash_go_back}
          </NoFocusButton>
        </Flex>
      </Flex>
    )
  }

  // Error message
  if (hasWebSocketError) {
    return (
      <Flex direction="column" align="center" justify="center" height="300px" px="20px">
        <Box
          backgroundColor="#fee"
          border="1px solid #fcc"
          borderRadius="12px"
          p="20px"
          textAlign="center"
          maxW="400px"
        >
          <Text fontSize="48px" mb="12px">
            ⚠️
          </Text>
          <Text fontSize="16px" fontWeight="500" mb="8px" color="#c33">
            Connection error
          </Text>
          <Text fontSize="14px" color="#666" mb="16px">
            Unable to connect to the server. Check your internet connection and try again.
          </Text>
          <Button
            backgroundColor="#0084ff"
            color="white"
            _hover={{ backgroundColor: '#0073e6' }}
            onClick={onReconnect}
            size="sm"
          >
            Reconnect
          </Button>
        </Box>
      </Flex>
    )
  }
  if (conversation.length === 0 && (!isWebSocketReady || isLoading)) {
    // If there are no messages and the socket is not ready or loading — show the avatar with a bubble
    return <BotThinkingBubble pop={memoizedPop} />
  }

  return (
    <Box flex="1" overflowY="auto" py="16px" px="15px">
      {memoizedConversation.map((message, index) => (
        <Message
          key={`${index}-${message.message.substring(0, 20)}`}
          message={message}
          index={index}
          pop={pop}
          isBotThinking={!!(isBotResponseInProgress || isLoading)}
        />
      ))}

      {(() => {
        if (!isBotResponseInProgress) return null

        const lastMessage = memoizedConversation[memoizedConversation.length - 1]

        const shouldShowThinkingMessage = !lastMessage || lastMessage.isUserMessage

        if (!shouldShowThinkingMessage) return null

        return (
          <Message
            key="bot-thinking"
            message={{ message: '', isUserMessage: false }}
            index={memoizedConversation.length}
            pop={pop}
            isBotThinking={true}
          />
        )
      })()}

      <div ref={messagesEndRef} />
    </Box>
  )
}
